import React, { useMemo, FC, useCallback } from 'react'
import { TextField } from '@mui/material'

import { DIGIT_ONLY } from '../../utils/regex'
import { conditionalClasses } from '../../utils/helper-functions'

import './otp-input.scss'
import { v4 } from 'uuid'

export const InputUuid = [v4(), v4(), v4(), v4(), v4(), v4()]

export type OTPProps = {
    value: string
    valueLength: number
    error?: string
    touched?: boolean
    name?: string
    onChange: (value: string) => void
}

const OTPInput: FC<OTPProps> = ({
    value,
    valueLength,
    onChange,
    error,
    touched,
    name,
}) => {
    const valueItems = useMemo(() => {
        const valueArray = value?.split('')
        const items: Array<string> = []

        for (let i = 0; i < valueLength; i++) {
            const char = valueArray[i]

            if (DIGIT_ONLY.test(char)) {
                items.push(char)
            } else {
                items.push('')
            }
        }

        return items
    }, [value, valueLength])

    const valueUuids = useMemo(() => {
        return Array(valueLength)
            .fill('')
            .map(() => v4())
    }, [valueLength])

    const handleNextInput = (target: HTMLElement) => {
        let targetId = target.id.split('-')
        let findNextEle = document.querySelector(
            `input[id=input-${+targetId[1] + 1}]`
        )
        const nextEle = findNextEle as HTMLInputElement | null
        nextEle?.focus()
    }

    const handlePrevInput = (target: HTMLElement) => {
        let targetId = target.id.split('-')
        let findPriviousEle = document.querySelector(
            `input[id=input-${+targetId[1] - 1}]`
        )
        const previousEle = findPriviousEle as HTMLInputElement | null
        previousEle?.focus()
    }

    const handleOnChange = useCallback(
        (
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            index: string
        ) => {
            const target = e.target
            let findIndex = parseInt(index.split('-')[1])
            let targetValue = target.value.trim()
            const isTargetValueDigit = DIGIT_ONLY.test(targetValue)

            let targetId = target.id.split('-')
            let findNextInputEle = document.querySelector(
                `input[id=input-${+targetId[1] + 1}]`
            )
            const nextInputEl = findNextInputEle as HTMLInputElement | null

            if (!isTargetValueDigit && targetValue !== '') {
                return
            }
            if (
                !isTargetValueDigit &&
                nextInputEl &&
                nextInputEl.value !== ''
            ) {
                return
            }

            targetValue = isTargetValueDigit ? targetValue : ' '

            const targetValueLength = targetValue.length

            if (targetValueLength === 1) {
                const newValue =
                    value.substring(0, findIndex) +
                    targetValue +
                    value.substring(findIndex + 1)
                onChange(newValue)
                if (!isTargetValueDigit) {
                    return
                }
                handleNextInput(target)
            } else if (targetValueLength === valueLength) {
                onChange(targetValue)
                target.blur()
            }
        },
        [onChange, value, valueLength]
    )

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const { key } = e
        const target = e.target as HTMLInputElement

        if (key === 'ArrowRight' || key === 'ArrowDown') {
            e.preventDefault()
            return handleNextInput(target)
        }

        if (key === 'ArrowLeft' || key === 'ArrowUp') {
            e.preventDefault()
            return handlePrevInput(target)
        }

        const targetValue = target.value
        target.setSelectionRange(0, targetValue.length)

        if (e.key !== 'Backspace' || targetValue !== '') {
            return
        }

        handlePrevInput(target)
    }

    return (
        <>
            <div className="otp-group">
                {valueItems.map((digit, index) => (
                    <React.Fragment key={valueUuids[index]}>
                        <TextField
                            id={`input-${index}`}
                            className={`otp-input ${conditionalClasses(
                                !!touched && !!error,
                                'errorBorder'
                            )}`}
                            name={name}
                            variant="outlined"
                            type="text"
                            autoComplete="off"
                            inputProps={{
                                maxLength: valueLength,
                                inputMode: 'numeric',
                                onKeyDown: handleKeyDown,
                            }}
                            value={digit}
                            onChange={(e) =>
                                handleOnChange(e, `input-${index}`)
                            }
                            error
                            autoFocus={index === 0}
                        />
                    </React.Fragment>
                ))}
            </div>
            {touched && !!error && <div className="error-txt">{error}</div>}
        </>
    )
}

export default OTPInput
